import type { SupportedLanguage } from 'locales/i18n';

export interface TravelKitLinkParams {
  adviserId: string;
  themeColor?: string;
  language: string;
}

export function getTravelKitLink(
  params: TravelKitLinkParams,
): string | undefined {
  const adviserId = params.adviserId.trim();
  if (!adviserId) return undefined;
  const token = encodeToken(
    compressObjectId(adviserId),
    themeFor(params.themeColor),
    langFor(params.language),
  );
  return `${travelKitBaseUrl()}/?p=${token}`;
}

type TravelKitTheme = 'bl' | 'or' | 'pr' | 'gr';
type TravelKitLang = 'en' | 'sc' | 'tc';

const THEME_BY_COLOR: Record<string, TravelKitTheme> = {
  blue: 'bl',
  orange: 'or',
  violet: 'pr',
  green: 'gr',
  earth: 'or',
};
const DEFAULT_THEME: TravelKitTheme = 'bl';

const LANG_BY_LANGUAGE: Record<SupportedLanguage, TravelKitLang> = {
  en: 'en',
  zh: 'sc',
  'zh-Hant-HK': 'tc',
};
const DEFAULT_LANG: TravelKitLang = 'tc';

const TRAVELKIT_TOKEN_VERSION = 1;
const TOKEN_COUNTRY = 'JP';
const TOKEN_NO_INSURER = '_';
const TOKEN_DEFAULT_ORIGIN = 'hk';

const TRAVELKIT_PRODUCTION_URL = 'https://sos.portfoplus.com';
const TRAVELKIT_DEV_PORT = '3021';

function themeFor(themeColor: string | undefined): TravelKitTheme {
  return (themeColor && THEME_BY_COLOR[themeColor]) || DEFAULT_THEME;
}

function langFor(language: string): TravelKitLang {
  return (
    (LANG_BY_LANGUAGE as Record<string, TravelKitLang | undefined>)[language] ??
    DEFAULT_LANG
  );
}

function encodeToken(
  ref: string,
  theme: TravelKitTheme,
  lang: TravelKitLang,
): string {
  const head = `${TRAVELKIT_TOKEN_VERSION}.${TOKEN_COUNTRY}.${TOKEN_NO_INSURER}.${ref}.${theme}`;
  // lang `tc` drops origin+lang (page defaults). Else `.hk.<lang>`.
  return lang === 'tc' ? head : `${head}.${TOKEN_DEFAULT_ORIGIN}.${lang}`;
}

function compressObjectId(hexId: string): string {
  const trimmed = hexId.trim();
  if (trimmed.length !== 24 || !/^[0-9a-fA-F]{24}$/.test(trimmed)) {
    return trimmed;
  }

  const bytes: number[] = [];
  for (let i = 0; i < 24; i += 2) {
    bytes.push(parseInt(trimmed.slice(i, i + 2), 16));
  }
  let bin = '';
  for (let i = 0; i < bytes.length; i++) {
    bin += String.fromCharCode(bytes[i]);
  }
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function travelKitBaseUrl(): string {
  return resolveBaseUrl(
    process.env.REACT_APP_PFPLUS_TRAVELKIT_URL,
    process.env.REACT_APP_PFPLUS_FRONTEND_URL,
  );
}

function unquote(value: string): string {
  return value.trim().replace(/^["']|["']$/g, '');
}

function stripTrailingSlash(url: string): string {
  return url.replace(/\/+$/, '');
}

function resolveBaseUrl(
  explicit: string | undefined,
  profilePlusAppUrl: string | undefined,
): string {
  const cleaned = unquote(explicit || '');
  if (cleaned) return stripTrailingSlash(cleaned);

  const appUrl = unquote(profilePlusAppUrl || '');
  if (appUrl) {
    try {
      const url = new URL(appUrl);
      if (url.hostname === 'localhost' || url.hostname === '127.0.0.1') {
        return `${url.protocol}//${url.hostname}:${TRAVELKIT_DEV_PORT}`;
      }
      const travelKitHost = url.hostname.replace(
        /^((?:[a-z0-9]+-)?)app\./i,
        '$1sos.',
      );
      if (travelKitHost !== url.hostname) {
        return `${url.protocol}//${travelKitHost}`;
      }
    } catch {}
  }

  return TRAVELKIT_PRODUCTION_URL;
}
